/**
 * Uses Swiper to play a video carousel with autoplay
 * On hover, the video will pause on the sameslide and loop, and on mouseleave, the video will autoplay to the next slides
 * if the youtube button is clicked, it will open a modal and play the youtube video and pause the autoplay
 * @class VideoCarouselNoGIF
 * @extends HTMLElement
 */

import Swiper from 'swiper';
import { Pagination, Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import LazyLoad from 'vanilla-lazyload';

class VideoHeroCarousel extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    const context = this;
    const swiper = new Swiper(this, {
      // Optional parameters
      slidesPerView: 1,
      modules: [Pagination, Navigation, Autoplay],
      loop: true,
      pagination: {
        el: this.querySelector('.hero-pagination'),
        clickable: true,
      },
      navigation: {
        nextEl: '.swiper-video-next',
        prevEl: '.swiper-video-prev',
      },
      on: {
        init: function () {
          startVideo(this.slides[this.activeIndex]);
          setTimeout(() => {
            context
              .querySelectorAll('.video-carousel video')
              .forEach((elem, index) => {
                if (this.slides.length === 1) {
                  elem.setAttribute('loop', true);
                  elem.play();
                } else if (window.innerWidth >= 768 && index != 0) {
                  elem.play();
                  setTimeout(() => {
                    elem.pause();
                    elem.currentTime = 0;
                  }, 10);
                } else if (window.innerWidth < 768 && index != 1) {
                  elem.play();
                  setTimeout(() => {
                    elem.pause();
                    elem.currentTime = 0;
                  }, 10);
                }
              });
          }, 100);
        },
        slideChangeTransitionEnd: function () {
          startVideo(this.slides[this.activeIndex]);
        },
      },
      autoplay: {
        delay: 2850,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      },
    });

    // Inject the YouTube video player when the button is clicked.
    const injectYouTubePlayer = () => {
      // Replace 'YOUR_YOUTUBE_VIDEO_ID' with the actual ID of the YouTube video.
      var videoId =
        this.querySelector('#youtube-player').getAttribute('youtube-url');

      // Construct the YouTube embed URL with the video ID and any desired parameters.

      // Create the iframe element with the YouTube video embedded.
      var iframe = document.createElement('iframe');
      iframe.className = 'top-0 absolute left-0 w-full h-full border-0 z-50';
      iframe.setAttribute('src', videoId);
      iframe.setAttribute('title', 'YouTube video player');
      iframe.setAttribute('allow', 'autoplay'); // Allow autoplay without cookies.

      // Add the iframe to the container or location where you want to display the video.
      var container = this.querySelector('#youtube-player');
      container.appendChild(iframe);
    };

    this.querySelector('animated-button')?.addEventListener('click', (e) => {
      //if youtube button existss
      if (this.querySelector('.youtube-button')) {
        e.preventDefault();
        this.querySelector('.youtube-modal').classList.remove('hidden');
        injectYouTubePlayer();
        // Pause the autoplay when the YouTube video is playing.
        swiper.autoplay.pause();
        swiper.slides[swiper.activeIndex]
          .querySelector('[data-video-desktop-index]')
          .setAttribute('loop', true);
        swiper.slides[swiper.activeIndex]
          .querySelector('[data-video-mobile-index]')
          .setAttribute('loop', true);
      }
    });

    // Handle case where animated button doesn't exist but youtube button does
    if (
      !this.querySelector('animated-button') &&
      this.querySelector('.youtube-button')
    ) {
      this.querySelector('.youtube-button').addEventListener('click', (e) => {
        e.preventDefault();
        this.querySelector('.youtube-modal').classList.remove('hidden');
        injectYouTubePlayer();
        // Pause the autoplay when the YouTube video is playing.
        swiper.autoplay.pause();
        swiper.slides[swiper.activeIndex]
          .querySelector('[data-video-desktop-index]')
          .setAttribute('loop', true);
        swiper.slides[swiper.activeIndex]
          .querySelector('[data-video-mobile-index]')
          .setAttribute('loop', true);
      });
    }

    if (this.querySelector('.youtube-modal')) {
      this.querySelector('.youtube-modal').addEventListener('click', (e) => {
        if (!e.target.classList.contains('video-container')) {
          if (this.querySelector('.youtube-modal')) {
            this.querySelector('.youtube-modal').classList.add('hidden');
            // Resume the autoplay when the YouTube video is closed.
            swiper.autoplay.resume();

            swiper.slides[swiper.activeIndex]
              .querySelector('[data-video-desktop-index]')
              .removeAttribute('loop');
            swiper.slides[swiper.activeIndex]
              .querySelector('[data-video-mobile-index]')
              .removeAttribute('loop');
          }

          this.querySelector('iframe').remove();
        }
      });
    }

    context
      .querySelector('.swiper-video-prev')
      .addEventListener('click', function () {
        // context
        //   .querySelector('.swiper-slide-prev')
        //   .querySelector('[data-video-desktop-index]')
        //   .removeAttribute('loop')
        context
          .querySelector('.swiper-slide-next')
          .querySelector('[data-video-desktop-index]')
          .removeAttribute('loop');

        swiper.slides[swiper.activeIndex]
          .querySelector('[data-video-desktop-index]')
          .setAttribute('loop', true);
      });

    context
      .querySelector('.swiper-video-next')
      .addEventListener('click', function () {
        context
          .querySelector('.swiper-slide-prev')
          .querySelector('[data-video-desktop-index]')
          .removeAttribute('loop');
        context
          .querySelector('.swiper-slide-next')
          .querySelector('[data-video-desktop-index]')
          .removeAttribute('loop');

        swiper.slides[swiper.activeIndex]
          .querySelector('[data-video-desktop-index]')
          .setAttribute('loop', true);
      });

    if (this.slides.length > 1) {
      context.addEventListener('mouseenter', function () {
        swiper.slides[swiper.activeIndex]
          .querySelector('[data-video-desktop-index]')
          .setAttribute('loop', true);
      });

      context.addEventListener('mouseleave', function () {
        swiper.slides[swiper.activeIndex]
          .querySelector('[data-video-desktop-index]')
          .removeAttribute('loop');
      });
    }

    function startVideo(slide) {
      var videoDesktop = slide.querySelector('[data-video-desktop-index]');
      var videoMobile = slide.querySelector('[data-video-mobile-index]');
      context.querySelectorAll('[data-video-desktop-index]').forEach((elem) => {
        elem.pause();
        elem.currentTime = 0;
      });
      context.querySelectorAll('[data-video-mobile-index]').forEach((elem) => {
        elem.pause();
        elem.currentTime = 0;
      });

      function videoSwitcherMobile() {
        if (!isHovering('video-carousel')) {
          swiper.slideNext();
        } else {
          videoMobile.play();
        }
      }

      // Track if user has interacted with the video on mobile
      let userInteractedWithVideo = false;

      // Handle user touch/click on mobile video - stop autoplay and loop video
      function handleMobileVideoInteraction() {
        if (window.innerWidth < 768 && !userInteractedWithVideo) {
          userInteractedWithVideo = true;
          swiper.autoplay.stop();
          videoMobile.setAttribute('loop', 'true');
        }
      }

      // Handle mobile video play - only act if user initiated
      function handleMobileVideoPlay() {
        if (window.innerWidth < 768 && userInteractedWithVideo) {
          swiper.autoplay.stop();
          videoMobile.setAttribute('loop', 'true');
        }
      }

      // Handle mobile video pause - user manually paused
      function handleMobileVideoPause() {
        if (window.innerWidth < 768) {
          // User paused the video - remove loop so it can end naturally if they resume
          // Keep autoplay stopped so they can resume watching
        }
      }

      // function videoSwitcherDesktop() {
      //   if (!isHovering('video-carousel')) {
      //     swiper.slideNext()
      //   } else {
      //     videoDesktop.play()
      //   }
      // }

      if (window.innerWidth >= 768) {
        videoMobile.removeEventListener('mousedown', videoSwitcherMobile, true);
        videoMobile.removeEventListener('click', handleMobileVideoInteraction);
        videoMobile.removeEventListener('touchstart', handleMobileVideoInteraction);
        videoMobile.removeEventListener('play', handleMobileVideoPlay);
        videoMobile.removeEventListener('pause', handleMobileVideoPause);
        videoDesktop.play();
        // videoDesktop.addEventListener('ended', videoSwitcherDesktop)
      } else {
        videoMobile.play();
        videoMobile.addEventListener('ended', videoSwitcherMobile);
        // Add listeners to pause autoplay when user interacts with video on mobile
        videoMobile.addEventListener('click', handleMobileVideoInteraction);
        videoMobile.addEventListener('touchstart', handleMobileVideoInteraction);
        videoMobile.addEventListener('play', handleMobileVideoPlay);
        videoMobile.addEventListener('pause', handleMobileVideoPause);
      }
      window.addEventListener('resize', (event) => {
        if (window.innerWidth >= 768) {
          videoMobile.removeEventListener(
            'mousedown',
            videoSwitcherMobile,
            true
          );
          videoMobile.removeEventListener('click', handleMobileVideoInteraction);
          videoMobile.removeEventListener('touchstart', handleMobileVideoInteraction);
          videoMobile.removeEventListener('play', handleMobileVideoPlay);
          videoMobile.removeEventListener('pause', handleMobileVideoPause);
          videoDesktop.play();
          // videoDesktop.addEventListener('ended', videoSwitcherDesktop)
        } else {
          // videoDesktop.removeEventListener(
          //   'mousedown',
          //   videoSwitcherDesktop,
          //   true
          // )
          videoMobile.play();
          videoMobile.addEventListener('ended', videoSwitcherMobile);
          videoMobile.addEventListener('click', handleMobileVideoInteraction);
          videoMobile.addEventListener('touchstart', handleMobileVideoInteraction);
          videoMobile.addEventListener('play', handleMobileVideoPlay);
          videoMobile.addEventListener('pause', handleMobileVideoPause);
        }
      });
    }

    function isHovering(selector) {
      var element = document.querySelector(selector);
      if (!element) {
        return false;
      }
      return element.matches(':hover');
    }
    const lazyLoadInstance = new LazyLoad({
      elements_selector: '.lazy',
      // ... more custom settings?
    });

    if (lazyLoadInstance) {
      lazyLoadInstance.update();
    }
  }
}
customElements.define('video-hero-carousel', VideoHeroCarousel);
